import type { StorySlice } from '../types/story-slice'
import type { ChainSeams } from '../types/chain-seams'
import type { ChainSeamMemo } from '../types/chain-seam-memo'
import type { StoryFrame } from '../types/story-frame'
import { paragraphsKey } from './paragraphs-key'

/**
 * The `seams` argument `storyFromChain` takes, rebuilt for `frames` from what this session recorded.
 *
 * An entry is trusted while the downstream frame's current content still STARTS WITH the text it was
 * recorded against — a prefix check, not exact equality (WMPCM-10397). That is the guard that makes
 * a cross-call memo safe, and it was originally exact equality on the assumption that an edit in the
 * downstream frame always coincides with the upstream frame being back at capacity, so the probe
 * would simply reproduce the seam again once invalidated. That holds when text cascades forward from
 * typing into the tail frame, but not when typing into a MIDDLE frame frees up room upstream instead
 * of filling it: exact equality then invalidated the entry on literally every keystroke, handing every
 * one to a probe that is provably blind whenever the upstream frame has slack (see `ChainSeamMemo`),
 * which read each new character as a fresh paragraph break and left it there permanently — one
 * orphaned paragraph per keystroke, rendering as one character per line.
 *
 * A prefix check fixes exactly that case without touching the probe at all: typing into a frame
 * appends to it, so its content before the appended text is unchanged, and the recorded seam for the
 * boundary immediately upstream of it stays valid across every keystroke instead of being thrown away
 * and re-guessed. An edit that is NOT a pure append — text removed, or changed before the point the
 * seam was recorded — naturally fails the prefix check (current content no longer starts with the
 * recorded one), so it still falls back to the probe exactly as before; this only ever makes MORE
 * cases trust the memo, never fewer.
 *
 * It also composes with `storyFromChain`'s own two guards rather than duplicating them: a
 * text-carrying frame inserted between the pair changes `frames[k + 1]` and fails this key check, and
 * a textless one shifts `upstreamIndex` so guard 1 rejects the memo anyway.
 *
 * **An empty recorded key is never trusted, prefix check or not.** `''` is a prefix of every string,
 * so treating it like any other recorded key would trust an entry against ANY future downstream
 * content — including the very next thing typed into a frame `distributeStory` just drained to empty
 * because everything fused upstream (its `!split` branch records `fragment: false` there, not because
 * the two halves are genuinely separate, but simply because there was no split to make). Left
 * untrusted, that manufactured `false` would out-rank a fresh, now-correct probe result on literally
 * the next keystroke and silently reintroduce the WMPCM-10397 fragmentation this memo exists to
 * prevent.
 */
export function chainSeamsFromMemo(
  memo: ChainSeamMemo,
  frames: readonly Pick<StoryFrame, 'id' | 'paragraphs'>[]
): ChainSeams {
  const seams: (boolean | undefined)[] = []

  for (let i = 0; i + 1 < frames.length; i++) {
    const entry = memo.get(frames[i]!.id)

    seams.push(
      entry != null &&
        entry.downstreamKey !== '' &&
        paragraphsKey(frames[i + 1]!.paragraphs).startsWith(entry.downstreamKey)
        ? entry.fragment
        : undefined
    )
  }

  return seams
}

/**
 * Records what a distribution just decided about each boundary, so the next one does not have to
 * re-derive it by probe.
 *
 * Call it with the distribution's FULL slice list, never `changedSlices`' filtered one — an
 * unchanged downstream frame still needs its fingerprint recorded, or the very next assembly finds
 * no entry and falls back to the probe.
 *
 * A boundary whose seam is `undefined` has its entry **deleted**, not skipped. That is how unlink
 * retires the boundary it just cut: the two sides are separate stories from that moment, and a
 * lingering entry would claim a seam between them.
 */
export function recordChainSeams(
  memo: ChainSeamMemo,
  frames: readonly Pick<StoryFrame, 'id'>[],
  slices: readonly StorySlice[],
  seams: readonly (boolean | undefined)[]
): void {
  const byId = new Map(slices.map((slice) => [slice.id, slice]))

  for (let i = 0; i + 1 < frames.length; i++) {
    const upstreamId = frames[i]!.id
    const fragment = seams[i]
    // Looked up by id rather than by index: a caller may distribute over a subset of the window it
    // records against (the delete repair distributes over survivors alone), so positions can differ.
    const downstream = byId.get(frames[i + 1]!.id)

    if (fragment == null || !downstream) {
      memo.delete(upstreamId)
      continue
    }

    memo.set(upstreamId, { downstreamKey: paragraphsKey(downstream.paragraphs), fragment })
  }
}
